import 'youbora-adapter-videojs';
import type { VideoJsPlayer } from 'video.js';

import type { AnalyticsMode, PlayerSettings } from '../../player.interface';

import { useEffect, useMemo } from 'react';
import * as youbora from 'youboralib';

import { useLogger } from '../../../../contexts/logger-context';
import { getPackageVersion } from '../../../../utils/version';
import { VideoJsNativeEvent } from '../../videojs-event';

const MiAdapter = youbora.adapters.Videojs.extend({
  getPlayerVersion: getPackageVersion,
});

const getReferrerInfo = (): string => {
  return document.referrer ? document.referrer : 'Direct access without referrer / Referrer information not available';
};

const getYouboraPlugin = (config: youbora.YouboraPluginConfig, analyticsMode: AnalyticsMode) => {
  const initialOptions: youbora.YouboraPluginConfig = { accountCode: config.accountCode };

  if (config.host) {
    initialOptions.host = config.host;
  }

  if (analyticsMode === 'minimal') {
    initialOptions.disableStorage = true;
  }

  const pluginInstance = new youbora.Plugin(initialOptions);
  youbora.Util.logAllEvents(pluginInstance);

  return pluginInstance;
};

export function useYouboraAnalytics(
  player: VideoJsPlayer,
  settings: PlayerSettings,
  analyticsMode: AnalyticsMode,
  config?: youbora.YouboraPluginConfig
): void {
  const { logger: mainLogger } = useLogger();
  const logger = useMemo(() => mainLogger.getSubLogger({ name: 'YouboraAnalytics' }), [mainLogger]);

  useEffect(() => {
    if (!config) {
      return;
    }

    let isSeeking = false;
    let lastTime = 0;
    let firstFullPlayEnded = false;

    const onLoadedMetadata = () => {
      isSeeking = false;
      lastTime = 0;
      firstFullPlayEnded = false;

      // If autoplay is enabled and muted, disable the plugin to prevent automatic tracking
      if (settings.autoplay && settings.muted) {
        logger.info('Autoplay and muted settings are enabled, disabling Youbora plugin');
        plugin.disable();
      } else {
        logger.info('Enabling Youbora plugin');
        plugin.enable();
      }

      plugin.setOptions({
        'content.isLive': `${player.duration() === Infinity}`,
      });
    };

    const onVideoEnded = () => {
      if (plugin.options.enabled === false) {
        logger.debug('Youbora plugin is already disabled, skipping video ended handling');
        return;
      }

      logger.debug('Video ended, disabling Youbora plugin');
      plugin.disable();
      firstFullPlayEnded = true;
    };

    const onSeeking = () => {
      isSeeking = true;
    };

    const onSeeked = () => {
      isSeeking = false;
    };

    const onVolumeChange = () => {
      if (firstFullPlayEnded) {
        logger.debug('First full play ended, not toggling Youbora plugin on volume change');
        return;
      }

      // If the user unmutes the video, enable the plugin
      if (!player.muted()) {
        logger.debug('Video is unmuted, enabling Youbora plugin');
        plugin.enable();
      }
    };

    const onTimeupdate = () => {
      if (plugin.options.enabled === false) {
        logger.debug('Youbora plugin is disabled, skipping time update handling');
        return;
      }

      if (isSeeking) {
        return;
      }

      const currentTime = player.currentTime();
      const duration = player.duration();

      if (duration === Infinity) {
        logger.debug('Video is live, not tracking time updates');
        return;
      }

      // Detect automatic loop (currentTime jumps from near end back to near 0)
      if (lastTime > currentTime && lastTime > duration - 0.5 && currentTime < 0.5) {
        logger.debug('Automatic loop detected, disabling Youbora plugin');
        plugin.disable();
        firstFullPlayEnded = true;
      }

      lastTime = currentTime;
    };

    const options: youbora.YouboraPluginConfig = {
      'content.customDimension.4': document.location.href,
      'content.customDimension.5': getReferrerInfo(),
      ...config,
    };

    if (analyticsMode === 'minimal') {
      options['user.name'] = 'Unknown';
      options['device.id'] = 'Unknown';
      options['device.isAnonymous'] = true;
      options['network.ip'] = '0.0.0.0';
    }

    logger.info('Initializing Youbora analytics plugin', {
      host: config.host,
      analyticsMode,
      autoplay: settings.autoplay,
      muted: settings.muted,
      options,
      config,
      playerVersion: getPackageVersion(),
    });
    const plugin = getYouboraPlugin(config, analyticsMode);
    plugin.setOptions(options);
    plugin.setAdapter(new MiAdapter(player));

    player.on(VideoJsNativeEvent.LOADED_METADATA, onLoadedMetadata);
    // The ended event doesn't fire if the loop property is true
    player.on(VideoJsNativeEvent.ENDED, onVideoEnded);
    player.on(VideoJsNativeEvent.SEEKING, onSeeking);
    player.on(VideoJsNativeEvent.SEEKED, onSeeked);
    player.on(VideoJsNativeEvent.TIME_UPDATE, onTimeupdate);
    player.on(VideoJsNativeEvent.VOLUME_CHANGE, onVolumeChange);

    return () => {
      logger.debug('Disposing Youbora analytics plugin');

      player.off(VideoJsNativeEvent.LOADED_METADATA, onLoadedMetadata);
      player.off(VideoJsNativeEvent.ENDED, onVideoEnded);
      player.off(VideoJsNativeEvent.SEEKING, onSeeking);
      player.off(VideoJsNativeEvent.SEEKED, onSeeked);
      player.off(VideoJsNativeEvent.TIME_UPDATE, onTimeupdate);
      player.off(VideoJsNativeEvent.VOLUME_CHANGE, onVolumeChange);
      plugin.removeAdapter();
    };
  }, [logger, player, analyticsMode, config, settings.autoplay, settings.muted]);
}
